<script setup>
import { computed, inject } from 'vue';
import { groomingFreshness } from '@/lib/grooming.js';
import { styleVars } from './shared.js';

const props = defineProps({
    config: { type: Object, required: true },
    accent: { type: String, default: '' },
    org: { type: Object, default: () => ({}) },
    site_data: { type: Object, default: () => ({}) },
});

const CONDITION = {
    open: { label: 'Open', tone: 'open' },
    caution: { label: 'Caution', tone: 'caution' },
    partial: { label: 'Some closed', tone: 'caution' },
    closed: { label: 'Closed', tone: 'closed' },
    maintenance: { label: 'Maintenance', tone: 'closed' },
};

const groomed = (t) => !!t.grooming && t.grooming !== 'none';

// No grooming report is no block for a visitor, heading included; the
// editor keeps its line (Mus, 2026-10-11).
const editing = inject('tiqEditorCanvas', false);
const rows = computed(() => {
    const ids = (Array.isArray(props.config.network_ids) ? props.config.network_ids : []).map(String);
    const groups = (props.site_data?.trail_groups || [])
        .filter((g) => !ids.length || ids.includes(String(g.network_id)));
    const out = [];
    for (const g of groups) {
        for (const t of g.trails || []) {
            if (!props.config.show_ungroomed && !groomed(t)) continue;
            out.push({ ...t, network_name: g.network_name, network_condition: g.network_condition });
        }
    }
    return out;
});

// The pill is the grooming state: the same decaying "Groomed 3h ago"
// the Classic trails list wears, then a neutral line once it has gone
// stale, so a groom from last week never reads as this morning's.
function groomPill(t) {
    const f = groomingFreshness(t.groomed_at, t.grooming);
    if (f) return { label: f.label, tone: f.fresh ? 'open' : 'low' };
    if (!groomed(t)) return { label: 'Not groomed', tone: 'closed' };
    return { label: 'Not groomed today', tone: 'low' };
}

function condition(t) {
    const c = t.condition || t.network_condition;
    return CONDITION[c] || null;
}

function detail(t) {
    if (t.condition_note) return t.condition_note;
    if (t.grooming === 'partial') return 'Partly groomed.';
    return '';
}

function meta(t) {
    return [t.primary_use, t.difficulty, t.network_name].filter(Boolean).join(' · ');
}
</script>

<template>
    <section v-if="rows.length || editing" :id="config.anchor_id || null" class="th-block th-grooming th-sec" :style="styleVars(config)">
        <div class="th-wrap">
            <div v-if="config.heading || config.summary" class="th-grooming__head">
                <h2 v-if="config.heading" class="th-h2 th-h2--sm">{{ config.heading }}</h2>
                <span v-if="config.summary" class="th-mono th-grooming__summary">{{ config.summary }}</span>
            </div>
            <div v-if="rows.length" class="th-grid th-grooming__grid">
                <div v-for="t in rows" :key="t.id" class="th-card th-grooming__card">
                    <div class="th-grooming__top">
                        <div class="th-grooming__name">{{ t.name }}</div>
                        <span class="th-pill" :class="`th-pill--${groomPill(t).tone}`"><span class="th-dot"></span>{{ groomPill(t).label }}</span>
                    </div>
                    <div v-if="detail(t)" class="th-grooming__detail">{{ detail(t) }}</div>
                    <div class="th-grooming__foot">
                        <span v-if="condition(t)" class="th-grooming__cond">
                            <span class="th-dot" :class="`th-dot--${condition(t).tone}`"></span>{{ condition(t).label }}
                        </span>
                        <span v-if="meta(t)" class="th-mono th-grooming__meta">{{ meta(t) }}</span>
                    </div>
                </div>
            </div>
            <div v-else class="th-card th-grooming__empty">No trails have a grooming report right now.</div>
        </div>
    </section>
</template>

<style scoped>
.th-grooming__head {
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    align-items: baseline;
    gap: 8px;
    margin-bottom: 16px;
}
.th-grooming__summary { text-transform: uppercase; }
.th-grooming__card { padding: 18px 20px; display: flex; flex-direction: column; gap: 8px; }
.th-grooming__top { display: flex; justify-content: space-between; gap: 10px; align-items: flex-start; }
.th-grooming__name { font: 700 18px/1.15 var(--th-display); }
.th-grooming__detail { font-size: 15px; }
.th-grooming__foot { display: flex; flex-wrap: wrap; align-items: center; gap: 6px 12px; }
.th-grooming__cond { display: inline-flex; align-items: center; gap: 6px; font-size: 13px; font-weight: 600; }
.th-grooming__meta { font-size: 11px; text-transform: uppercase; }
.th-grooming__empty { color: var(--th-muted); }
</style>
